import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import {
  Check,
  CheckCircle,
  CircleNotch,
  Cpu,
  WarningCircle
} from '@phosphor-icons/react';

const CapabilityNode = memo(({ data, selected }) => {
  const executionStatus = data.executionStatus;

  return (
    <div
      data-testid="flow-node-capability"
      className={`
        group relative min-w-[200px] max-w-[240px] rounded-[10px] overflow-hidden
        bg-[var(--surface-2)] border-l-[3px] border transition-all duration-150
        ${executionStatus === 'processing'
          ? 'border-l-purple-400 border-purple-400/40'
          : executionStatus === 'success'
            ? 'border-l-emerald-400 border-emerald-400/40'
            : selected
              ? 'border-l-purple-400 border-[var(--border-strong)]'
              : 'border-l-purple-500 border-[var(--border-default)] hover:border-[var(--border-strong)]'}
      `}
    >
      <Handle
        type="target"
        position={Position.Left}
        className="!w-[10px] !h-[10px] !border-2 !left-[-6px] transition-colors
          !bg-purple-400 !border-purple-300/50 hover:!bg-purple-300"
      />

      {/* Header */}
      <div className="flex items-center gap-2.5 px-3 pt-3 pb-2">
        <div className={`flex-shrink-0 w-[34px] h-[34px] rounded-lg border flex items-center justify-center
          ${executionStatus === 'success'
            ? 'bg-emerald-500/10 border-emerald-500/20'
            : executionStatus === 'processing'
              ? 'bg-purple-500/15 border-purple-500/25'
              : 'bg-purple-500/10 border-purple-500/20'}`}
        >
          {executionStatus === 'processing' ? (
            <CircleNotch size={20} weight="bold" className="text-purple-400 animate-spin" />
          ) : executionStatus === 'success' ? (
            <CheckCircle size={20} weight="duotone" className="text-emerald-400" />
          ) : (
            <Cpu size={20} weight="duotone" className="text-purple-400" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-white truncate leading-tight">{data.label}</p>
          <span className="text-[10px] text-purple-400/60 font-medium uppercase tracking-wider">Capability</span>
        </div>
      </div>

      {/* Body - status badges */}
      <div className="px-3 pb-2.5 pt-0.5 border-t border-white/[0.04]">
        <div className="flex items-center gap-1.5 flex-wrap">
          {executionStatus === 'processing' ? (
            <span className="text-[10px] text-purple-400 font-medium animate-pulse">Processing...</span>
          ) : executionStatus === 'success' ? (
            <span className="text-[10px] text-emerald-400 font-medium">Complete</span>
          ) : (
            <>
              {data.required ? (
                <span className="inline-flex items-center px-1.5 py-px text-[9px] font-semibold rounded bg-purple-500/15 text-purple-400 border border-purple-500/20 uppercase tracking-wider">Required</span>
              ) : (
                <span className="inline-flex items-center px-1.5 py-px text-[9px] font-semibold rounded bg-slate-500/15 text-slate-500 border border-slate-500/15 uppercase tracking-wider">Optional</span>
              )}
              {data.bound ? (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-px text-[9px] font-semibold rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
                  <Check size={8} weight="bold" /> Configured
                </span>
              ) : (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-px text-[9px] font-semibold rounded bg-amber-500/15 text-amber-400 border border-amber-500/20">
                  <WarningCircle size={8} weight="bold" /> Setup needed
                </span>
              )}
            </>
          )}
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Right}
        className="!w-[10px] !h-[10px] !border-2 !right-[-6px] transition-colors
          !bg-purple-400 !border-purple-300/50 hover:!bg-purple-300"
      />
    </div>
  );
});

CapabilityNode.displayName = 'CapabilityNode';
export default CapabilityNode;
